import type {
  HeatmapTile,
  HeatmapTileData,
  HeatmapTileSeverity,
  HeatmapTileTrend,
} from "~types/types";

type HeatmapContainer<T> = {
  data: Array<HeatmapTile<T>>;
  xBeginning: number;
  yBeginning: number;
  totalWidth: number;
  totalHeight: number;
};

const MARKETS_SEVERITY: HeatmapTileSeverity[] = ["low", "medium", "high"];

const getSeverity = (value: number, min: number, max: number) => {
  const maxAllowed = Math.min(3, MARKETS_SEVERITY.length);

  const range = max - min;
  const bucketSize = range / maxAllowed;

  if (value >= max) return MARKETS_SEVERITY[maxAllowed - 1];

  for (let i = 1; i < maxAllowed; i++) {
    if (value < min + i * bucketSize) return MARKETS_SEVERITY[i - 1];
  }

  return MARKETS_SEVERITY[maxAllowed - 1];
};

export const getTrend = (percentChange: number): HeatmapTileTrend => {
  if (percentChange > 0) return "positive";
  if (percentChange < 0) return "negative";

  return "neutral";
};

export const getChangeSeverity = (value: number, maxValue = 3) => {
  if (value === 0) return "neutral";

  return getSeverity(Math.abs(value), 0, maxValue);
};

const getMaximum = (array: Array<number>) => Math.max(...array);

const getMinimum = (array: Array<number>) => Math.min(...array);

const sumReducer = (acc: number, cur: number) => acc + cur;

const roundValue = (number: number) =>
  Math.max(Math.round(number * 100) / 100, 0);

const worstRatio = (row: Array<number>, width: number) => {
  const sum = row.reduce(sumReducer, 0);
  const rowMax = getMaximum(row);
  const rowMin = getMinimum(row);

  return Math.max(
    (width ** 2 * rowMax) / sum ** 2,
    sum ** 2 / (width ** 2 * rowMin)
  );
};

const getMinWidth = <T>(container: HeatmapContainer<T>) => {
  if (container.totalHeight ** 2 > container.totalWidth ** 2) {
    return { value: container.totalWidth, vertical: false };
  }

  return { value: container.totalHeight, vertical: true };
};

const validateArguments = <T>(
  data: Array<HeatmapTileData<T>>,
  width: number,
  height: number
) => {
  if (!width || width < 0) {
    return false;
  }

  if (!height || height < 0) {
    return false;
  }

  if (
    !data ||
    !Array.isArray(data) ||
    data.length === 0 ||
    !data.every((dataPoint) =>
      Object.prototype.hasOwnProperty.call(dataPoint, "size")
    )
  ) {
    return false;
  }

  return true;
};

export function prepareHeatmapTiles<T>(
  dataSeries: Array<HeatmapTileData<T>>,
  containerWidth: number,
  containerHeight: number
): Array<HeatmapTile<T>> {
  if (!validateArguments(dataSeries, containerWidth, containerHeight)) {
    return [];
  }

  const container: HeatmapContainer<T> = {
    data: [],
    xBeginning: 0,
    yBeginning: 0,
    totalWidth: containerWidth,
    totalHeight: containerHeight,
  };

  const initialData = dataSeries;
  const totalValue = dataSeries
    .map((dataPoint) => dataPoint.size)
    .reduce(sumReducer, 0);

  const dataScaled = dataSeries.map(
    (dataPoint) =>
      (dataPoint.size * containerHeight * containerWidth) / totalValue
  );

  const layoutRow = (row: Array<number>, width: number, vertical: boolean) => {
    const rowHeight = row.reduce(sumReducer, 0) / width;

    row.forEach((rowItem) => {
      const rowWidth = rowItem / rowHeight;
      const { xBeginning } = container;
      const { yBeginning } = container;

      let rowData: HeatmapTile<T>;

      if (vertical) {
        rowData = {
          x: xBeginning,
          y: yBeginning,
          width: rowHeight,
          height: rowWidth,
          data: initialData[container.data.length],
        };
        container.yBeginning += rowWidth;
      } else {
        rowData = {
          x: xBeginning,
          y: yBeginning,
          width: rowWidth,
          height: rowHeight,
          data: initialData[container.data.length],
        };
        container.xBeginning += rowWidth;
      }

      container.data.push(rowData);
    });

    if (vertical) {
      container.xBeginning += rowHeight;
      container.yBeginning -= width;
      container.totalWidth -= rowHeight;
    } else {
      container.xBeginning -= width;
      container.yBeginning += rowHeight;
      container.totalHeight -= rowHeight;
    }
  };

  const layoutLastRow = (
    rows: Array<number>,
    children: Array<number>,
    width: number
  ) => {
    const { vertical } = getMinWidth(container);

    layoutRow(rows, width, vertical);
    layoutRow(children, width, vertical);
  };

  const createTile = (
    children: Array<number>,
    row: Array<number>,
    width: number
  ): void => {
    if (children.length === 1) {
      return layoutLastRow(row, children, width);
    }

    const rowWithChild = [...row, children[0]];

    if (
      row.length === 0 ||
      worstRatio(row, width) >= worstRatio(rowWithChild, width)
    ) {
      children.shift();

      return createTile(children, rowWithChild, width);
    }
    layoutRow(row, width, getMinWidth(container).vertical);

    return createTile(children, [], getMinWidth(container).value);
  };

  createTile(dataScaled, [], getMinWidth(container).value);

  return container.data.map((dataPoint) => ({
    ...dataPoint,
    x: roundValue(dataPoint.x),
    y: roundValue(dataPoint.y),
    width: roundValue(dataPoint.width),
    height: roundValue(dataPoint.height),
  }));
}
